import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AxiosError } from 'axios';

import { warehouses } from 'constants/warehouses';

import { S3Item } from 'types';
import { SelectionItem, SelectionList } from 'types/api/selection.type';

import { useAddItemMutation } from 'redux/api/ordersApi';
import { useAppSelector } from 'redux/hooks';

import { selectionDataService } from 'services/selection.service';

import { BucketContext } from 'contexts';

import { Sticker } from 'components/Sticker';
import { QtySticker } from 'components/common/Qty/Qty';

import * as S from './Item.styles';

type ItemProps = {
  data: SelectionItem;
  index: number;
  listSet: Dispatch<SetStateAction<SelectionList | undefined>>;
};

export function Item(props: ItemProps): React.ReactElement {
  const { data, index, listSet } = props;
  const { getUrl } = useContext(BucketContext);
  const [image, imageSet] = useState<string>('');
  const { orderId, warehouse } = useAppSelector((state) => state.settings);
  const isActive = data?.active;
  const isSimpleCatalogue = warehouse.code === warehouses[0].code;
  const qty = data.quantity;
  const qtyBiggerThanZero = qty > 0;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [addItem] = useAddItemMutation();

  const orderItem = () => {
    if (!orderId) {
      toast.error(t('order.noSelectedCreate'));
      return;
    }

    addItem({
      orderId,
      data: {
        productCode: data.itemCodes?.split(' ')[1] ?? '',
        mainFabricCode: data.mainFabricCode,
        colorCode: data.itemColorCode,
        sizes: [],
      },
    })
      .unwrap()
      .then(() => {
        toast.success(t('product.orderedSuccessfully'));
      })
      .catch((err) =>
        toast.error(
          err.data?.apierror?.status === 'NOT_FOUND'
            ? t('product.notAvailable')
            : err.data?.apierror?.message ?? t('error.generic')
        )
      );
  };

  const removeItemFromList = () => {
    listSet((state) => {
      if (!state) return undefined;

      const newItems = { ...state.items };
      delete newItems[data.productIndex.toString()];

      const newItemsIds = state.itemsIds.filter((item) => item !== data.productIndex);

      if (newItemsIds.length === 0) {
        return undefined;
      }

      return { items: newItems, itemsIds: newItemsIds };
    });
  };

  const deleteItem = () => {
    selectionDataService
      .deleteProduct(data.id)
      .then(() => {
        toast.success(t('product.deletedSuccessfully'));
        removeItemFromList();
      })
      .catch((err: AxiosError) => {
        if (err?.response?.status === 404) {
          toast.success(t('product.deletedSuccessfully'));
          removeItemFromList();
        } else {
          toast.error(t('product.errorDeleting'));
        }
      });
  };

  useEffect(() => {
    if (!data?.imageName) return;

    getUrl(`catalogue/products${data.imageName}`).then((res: S3Item) => {
      imageSet(res.url);
    });
  }, [data?.imageName]);

  return (
    <Draggable draggableId={data?.productIndex.toString()} index={index}>
      {(provided) => (
        <S.Container {...provided.draggableProps} ref={provided.innerRef}>
          <S.ImageContainer>
            <S.DragButton iconLeft="FiMoreVertical" {...provided.dragHandleProps} />

            {image ? (
              <S.Img source={image} alt={data?.mainTitle} opacity={isActive ? '1' : '0.35'}>
                {!isActive ? (
                  <S.InactiveLayer>
                    <Sticker>{t('product.cancelled')}</Sticker>
                  </S.InactiveLayer>
                ) : undefined}
                {!qtyBiggerThanZero && !isSimpleCatalogue && (
                  <S.OutOfStock>
                    <Sticker>{t('product.outOfStock')}</Sticker>
                  </S.OutOfStock>
                )}
                {data.onlyPrice === 'true' && (
                  <S.OnlyPrice>
                    <Sticker>{t('product.onlyPrice')}</Sticker>
                  </S.OnlyPrice>
                )}
              </S.Img>
            ) : null}
          </S.ImageContainer>

          {qty ? <QtySticker qty={qty} /> : undefined}

          {data.mainTitle ? (
            <S.Title
              textTransform="uppercase"
              fontFamily="LFT Zeno Light"
              fontWeight="extraLight"
              scale="s"
              color="grayDark"
            >
              {data.mainTitle}
            </S.Title>
          ) : null}

          {data.itemCodes ? (
            <S.Title
              textTransform="uppercase"
              scale="s"
              fontFamily="LFT Zeno Light"
              fontWeight="extraLight"
              color="grayDark"
            >
              {data.itemCodes}
            </S.Title>
          ) : null}

          {data.colorCodes ? (
            <S.FlexContainer>
              {data.isSampleColor && (
                <S.TitleCode
                  textTransform="uppercase"
                  scale="s"
                  color="grayDark"
                  fontFamily="LFT Zeno Light"
                  fontWeight="extraLight"
                >
                  *
                </S.TitleCode>
              )}
              <S.TitleCode textTransform="uppercase" fontFamily="LFT Zeno" scale="s">
                {data.colorCodes.split(' ')[0]}
              </S.TitleCode>
              <S.TitleCode
                textTransform="uppercase"
                scale="s"
                color="grayDark"
                fontFamily="LFT Zeno Light"
                fontWeight="extraLight"
              >
                {data.colorCodes.split(' ').slice(1)}
              </S.TitleCode>
            </S.FlexContainer>
          ) : null}

          <S.Actions>
            <S.ActionButton
              fontFamily="LFT Zeno Light"
              fontWeight="extraLight"
              scale="xs"
              iconLeft="FiShoppingCart"
              motive="iconColumn"
              icoarea="1.6"
              onClick={() => orderItem()}
              disabled={!isActive}
            >
              {t('product.orderButton')}
            </S.ActionButton>

            <S.ActionButton
              fontFamily="LFT Zeno Light"
              fontWeight="extraLight"
              scale="xs"
              iconLeft="FiShoppingBag"
              motive="iconColumn"
              icoarea="1.6"
              onClick={() => {
                navigate(
                  `/catalogue/${data.genre}/${data.category}/${data.productId}/list?itemCode=${
                    data.itemCodes.split(' ')[1]
                  }&mainFabricCode=${data.mainFabricCode}&itemColorCode=${data.itemColorCode}`
                );
              }}
            >
              {t('product.variants')}
            </S.ActionButton>

            <S.ActionButton
              fontFamily="LFT Zeno Light"
              fontWeight="extraLight"
              scale="xs"
              iconLeft="FiX"
              motive="iconColumn"
              icoarea="1.6"
              onClick={() => deleteItem()}
            >
              {t('common.delete')}
            </S.ActionButton>
          </S.Actions>
        </S.Container>
      )}
    </Draggable>
  );
}
